import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Search, X, Plus, Download } from 'lucide-react';
import { useApp, FilterStatus, SortDir } from '@/store';
import { useT } from '@/lib/i18n';
import { fmtDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StatusDot } from '@/components/StatusBadge';
import { Status, type Customer } from '@/data/types';

const deviceCols = [
  'schaum-ab',
  'pulver-ab',
  'co2',
  'loeschposten',
  'fettbrand-ab',
  'lithium-ab',
] as const;

const customerTotal = (c: Customer) =>
  Object.values(c.deviceCounts ?? {}).reduce((s, v) => s + v, 0);

const getSortValue = (c: Customer, col: string): string | number => {
  if (col === 'city') return c.objectAddress?.city ?? '';
  if (col === 'total') return customerTotal(c);
  return (c as any)[col] ?? '';
};

export function Overview() {
  const t = useT();
  const nav = useNavigate();
  const {
    customers,
    search,
    filterStatus,
    filterWorker,
    sortCol,
    sortDir,
    selectedCustomerIds,
    toggleSelected,
    selectAll,
    setFilter,
    setSort,
    clearFilters,
    workers,
    addCustomer,
    toast,
  } = useApp();

  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newStatus, setNewStatus] = useState<Status | ''>('');
  const [newWorker, setNewWorker] = useState('');

  const handleExport = () => {
    const ids = Array.from(selectedCustomerIds);
    const subset = ids.length
      ? customers.filter(c => ids.includes(c.id))
      : customers;
    const blob = new Blob([JSON.stringify(subset, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fire-track-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleNewCustomer = () => {
    if (!newName.trim()) return;
    const id = `C-${Date.now()}`;
    addCustomer({
      id,
      name: newName.trim(),
      status: (newStatus as Status) || Status.NONE,
      worker: newWorker || undefined,
      extinguishers: [],
      hoses: [],
      archives: [],
    });
    toast(t('overview.newCustomer'));
    setNewOpen(false);
    setNewName('');
    setNewStatus('');
    setNewWorker('');
    nav(`/customer/${id}`);
  };

  const filtered = useMemo(() => {
    let list = customers.filter(c => {
      const q = search.toLowerCase().trim();
      if (q) {
        const hay =
          `${c.name} ${c.id} ${c.objectAddress?.street ?? ''} ${c.objectAddress?.city ?? ''} ${c.objectAddress?.zip ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (
        filterStatus !== FilterStatus.ALL &&
        (c.status as string) !== filterStatus
      )
        return false;
      if (filterWorker && c.worker !== filterWorker) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      const av = getSortValue(a, sortCol);
      const bv = getSortValue(b, sortCol);
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sortDir === SortDir.ASC ? cmp : -cmp;
    });
    return list;
  }, [customers, search, filterStatus, filterWorker, sortCol, sortDir]);

  const totals = useMemo(() => {
    const tot: Record<string, number> = {};
    filtered.forEach(c => {
      deviceCols.forEach(d => {
        tot[d] = (tot[d] ?? 0) + (c.deviceCounts?.[d] ?? 0);
      });
    });
    return tot;
  }, [filtered]);

  const grandTotal = useMemo(
    () => filtered.reduce((s, c) => s + customerTotal(c), 0),
    [filtered]
  );

  const allSelected =
    filtered.length > 0 && filtered.every(c => selectedCustomerIds.has(c.id));

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t('overview.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {filtered.length} of {customers.length} {t('overview.customers')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            data-focus="overview-export"
          >
            <Download className="h-4 w-4" />
            {t('overview.export')}
          </Button>
          <Button
            size="sm"
            onClick={() => setNewOpen(true)}
            data-focus="overview-new-customer"
          >
            <Plus className="h-4 w-4" />
            {t('overview.newCustomer')}
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
          <div className="sm:col-span-2 relative" data-focus="overview-search">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('overview.searchPlaceholder')}
              className="pl-9"
              value={search}
              onChange={e => setFilter('search', e.target.value)}
            />
          </div>
          <Select
            value={filterStatus}
            onValueChange={v => setFilter('filterStatus', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('table.status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('overview.allStatuses')}</SelectItem>
              <SelectItem value="done">{t('status.done')}</SelectItem>
              <SelectItem value="waiting">{t('status.waiting')}</SelectItem>
              <SelectItem value="todo">{t('status.todo')}</SelectItem>
              <SelectItem value="none">{t('status.none')}</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Select
              value={filterWorker || 'all'}
              onValueChange={v =>
                setFilter('filterWorker', v === 'all' ? '' : v)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder={t('customer.worker')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('overview.allWorkers')}</SelectItem>
                {workers.map(w => (
                  <SelectItem key={w.id} value={w.name}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              onClick={clearFilters}
              aria-label="Clear filters"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      <Card data-tour="overview-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={v => selectAll(!!v)}
                />
              </TableHead>
              <TableHead className="w-10">●</TableHead>
              <TableHead
                className="cursor-pointer hidden sm:table-cell"
                onClick={() => setSort('id')}
              >
                {t('table.id')}
              </TableHead>
              <TableHead
                className="cursor-pointer"
                onClick={() => setSort('name')}
              >
                {t('overview.company')}
              </TableHead>
              <TableHead className="hidden md:table-cell">
                {t('address.street')}
              </TableHead>
              <TableHead
                className="cursor-pointer hidden sm:table-cell"
                onClick={() => setSort('city')}
                data-focus="overview-city-sort"
              >
                {t('address.city')}
              </TableHead>
              <TableHead className="hidden lg:table-cell">
                {t('customer.worker')}
              </TableHead>
              <TableHead
                className="cursor-pointer hidden sm:table-cell"
                onClick={() => setSort('nextInspection')}
              >
                {t('overview.nextInsp')}
              </TableHead>
              {deviceCols.map(d => (
                <TableHead key={d} className="text-center hidden lg:table-cell">
                  {d}
                </TableHead>
              ))}
              <TableHead
                className="cursor-pointer text-center hidden lg:table-cell"
                onClick={() => setSort('total')}
                data-focus="overview-total"
              >
                {t('overview.total')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c: Customer) => (
              <TableRow
                key={c.id}
                data-state={
                  selectedCustomerIds.has(c.id) ? 'selected' : undefined
                }
              >
                <TableCell>
                  <Checkbox
                    checked={selectedCustomerIds.has(c.id)}
                    onCheckedChange={() => toggleSelected(c.id)}
                  />
                </TableCell>
                <TableCell>
                  <StatusDot status={c.status} />
                </TableCell>
                <TableCell className="font-mono text-xs hidden sm:table-cell">
                  {c.id}
                </TableCell>
                <TableCell>
                  <Link
                    to={`/customer/${c.id}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {c.name}
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {c.objectAddress?.street}
                </TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {c.objectAddress?.city}
                </TableCell>
                <TableCell className="text-muted-foreground hidden lg:table-cell">
                  {c.worker ?? '—'}
                </TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {fmtDate(c.nextInspection)}
                </TableCell>
                {deviceCols.map(d => (
                  <TableCell
                    key={d}
                    className="text-center hidden lg:table-cell"
                  >
                    {c.deviceCounts?.[d] ?? ''}
                  </TableCell>
                ))}
                <TableCell className="text-center font-semibold hidden lg:table-cell">
                  {customerTotal(c) || ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell
                colSpan={8}
                className="text-right hidden lg:table-cell"
              >
                Totals {/* i18n-ignore */}
              </TableCell>
              <TableCell colSpan={4} className="text-right lg:hidden">
                Totals {/* i18n-ignore */}
              </TableCell>
              {deviceCols.map(d => (
                <TableCell key={d} className="text-center hidden lg:table-cell">
                  {totals[d] || ''}
                </TableCell>
              ))}
              <TableCell className="text-center font-semibold hidden lg:table-cell">
                {grandTotal || ''}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </Card>
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('customer.newCustomerTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('overview.company')}</Label>
              <Input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleNewCustomer()}
              />
            </div>
            <div>
              <Label>{t('table.status')}</Label>
              <Select
                value={newStatus}
                onValueChange={v => setNewStatus(v as Status)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('overview.allStatuses')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={Status.TODO}>
                    {t('status.todo')}
                  </SelectItem>
                  <SelectItem value={Status.WAITING}>
                    {t('status.waiting')}
                  </SelectItem>
                  <SelectItem value={Status.DONE}>
                    {t('status.done')}
                  </SelectItem>
                  <SelectItem value={Status.NONE}>
                    {t('status.none')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('customer.worker')}</Label>
              <Select value={newWorker} onValueChange={setNewWorker}>
                <SelectTrigger>
                  <SelectValue placeholder={t('overview.allWorkers')} />
                </SelectTrigger>
                <SelectContent>
                  {workers.map(w => (
                    <SelectItem key={w.id} value={w.name}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleNewCustomer}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
